import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  Paper,
  Alert,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import ValueTypeEnums from './ValueTypeEnums';

interface AddValueContentProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<ValueContent>) => Promise<void>;
  initialData?: ValueContent | null;
  isEditing?: boolean;
}

const AddValueContent: React.FC<AddValueContentProps> = ({
  open,
  onClose,
  onSave,
  initialData = null,
  isEditing = false,
}) => {
  const theme = useTheme();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState({
    headline: initialData?.headline || '',
    content: initialData?.content || '',
    value_type: initialData?.value_type || ValueTypeEnums.CPO,
    applicable_for: initialData?.applicable_for || [],
    end_date: initialData?.end_date ? initialData.end_date.split('T')[0] : '',
    is_active: initialData?.is_active ?? true,
    label: initialData?.label || '',
  });

  const applicableForOptions = ['CPO', 'New', 'Used', 'All'];

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name as string]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleApplicableForChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      applicable_for: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const validateForm = () => {
    if (!formData.headline?.trim()) {
      setError('Headline is required');
      return false;
    }
    if (!formData.content?.trim()) {
      setError('Message content is required');
      return false;
    }
    if (!formData.value_type?.trim()) {
      setError('Value type is required');
      return false;
    }
    if (formData.applicable_for.length === 0) {
      setError('At least one applicable option must be selected');
      return false;
    }
    
    // Validate label field - optional but must be under 40 characters if provided
    if (formData.label && formData.label.length > 40) {
      setError('Label must be 40 characters or less');
      return false;
    }
    
    // Validate expiry date - now required
    if (!formData.end_date?.trim()) {
      setError('Expiry date is required');
      return false;
    }
    
    // Validate expiry date if provided
    if (formData.end_date) {
      const selectedDate = new Date(formData.end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (selectedDate < today) {
        setError('Expiry date cannot be in the past');
        return false;
      }
    }
    
    return true;
  };

  // Helper function to get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        headline: formData.headline.trim(),
        content: formData.content.trim(),
        value_type: formData.value_type,
        applicable_for: formData.applicable_for,
        end_date: formData.end_date || undefined,
        is_active: formData.is_active,
        label: formData.label?.trim() || undefined,
      };

      await onSave(payload);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save value content',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      headline: '',
      content: '',
      value_type: ValueTypeEnums.CPO,
      applicable_for: [],
      end_date: '',
      is_active: true,
      label: '',
    });
    setError('');
    onClose();
  };

  

  return (
    <ModalMAIQ
      open={open}
      onClose={handleClose}
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: '900px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight="600">
                {isEditing ? 'Edit Value Content' : 'Add New Value Content'}
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  color: 'white',
                  p: 0.5,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            p: 3,
          }}
        >
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Headline *
                </Typography>
                <TextField
                  fullWidth
                  name="headline"
                  value={formData.headline}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter headline for the value content"
                  size="small"
                  inputProps={{
                    sx: { py: 1.5 },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Message *
                </Typography>
                <TextField
                  fullWidth
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter the message content"
                  multiline
                  rows={4}
                  size="small"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Label
                </Typography>
                <TextField
                  fullWidth
                  name="label"
                  value={formData.label}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="Enter a short label (max 40 characters)"
                  size="small"
                  inputProps={{
                    maxLength: 40,
                    sx: { py: 1.5 },
                  }}
                  helperText={`${formData.label.length}/40 characters`}
                />
              </Box>
            </Grid>

            {/* Value Type and Applicable For */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Type *
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Type</InputLabel>
                  <Select
                    name="value_type"
                    value={formData.value_type}
                    onChange={handleChange}
                    label="Select Type"
                  >
                    {ValueTypeEnums.getEnumValues().map(valueType => (
                      <MenuItem key={valueType} value={valueType}>
                        {valueType.toUpperCase().replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Applicable For *
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Applicable Options</InputLabel>
                  <Select
                    multiple
                    value={formData.applicable_for}
                    onChange={handleApplicableForChange}
                    label="Select Applicable Options"
                    renderValue={selected => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(value => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            // sx={{
                            //   backgroundColor: getTypeColor(value),
                            //   color: 'white',
                            //   fontSize: '0.7rem',
                            //   height: 24,
                            // }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {applicableForOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        <Checkbox
                          checked={formData.applicable_for.indexOf(option) > -1}
                        />
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            {/* Expiry Date and Active Status */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Expiry Date *
                </Typography>
                                 <LocalizationProvider dateAdapter={AdapterDayjs}>
                   <DatePicker
                    //  label="Expiry Date"
                     value={formData.end_date ? dayjs(formData.end_date) : null}
                     onChange={(newValue: Dayjs | null) => {
                       setFormData(prev => ({
                         ...prev,
                         end_date: newValue ? newValue.format('YYYY-MM-DD') : '',
                       }));
                     }}
                     minDate={dayjs(getMinDate())}
                     slotProps={{
                       textField: {
                         fullWidth: true,
                         size: 'small',
                         helperText: "Required. Cannot select past dates",
                         inputProps: {
                           sx: { py: 1.5 },
                         },
                       },
                     }}
                   />
                 </LocalizationProvider>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  pt: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
          }}
        >
          <Tooltip title={isEditing ? 'Update value content' : 'Save new value content'}>
            <Button
              variant="contained"
              color="secondary"
              disabled={isSubmitting}
              onClick={handleSubmit}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                bgcolor: '#0F2A52',
                color: 'white',
                py: 0.75,
                px: 3,
                '&:hover': {
                  bgcolor: '#0a1f3d',
                },
              }}
            >
              {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
            </Button>
          </Tooltip>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddValueContent; 